import type { en } from './en';

/**
 * Português do Brasil. Values are copied **verbatim** from Android's
 * `values-pt-rBR/strings.xml` — not re-translated. Terminologia conforme o
 * glossário oficial do Android, e o bloco só-WebClip no fim segue o mesmo registro.
 *
 * This is the **longest** language we ship and therefore the real layout test:
 * anything that fits here fits everywhere. Do not shorten a string to make a
 * component behave — fix the component.
 *
 * `satisfies Record<keyof typeof en, string>` makes a missing key a build error.
 */
export const ptBR = {
  app_name: 'CheckLinks',

  nav_dashboard: 'Painel',
  nav_results: 'Resultados',
  nav_settings: 'Configurações',

  common_see_all: 'Ver tudo',
  common_cancel: 'Cancelar',
  value_none: '—',
  value_unknown: 'Desconhecido',
  unit_ms: '{0} ms',

  net_offline: 'Offline',
  net_no_connection_body: 'Sem conexão de rede — este aparelho não consegue verificar links.',

  home_progress_label: 'Progresso',
  home_percent: '{0}%',
  home_checked_of_total: '{0} / {1}',
  home_waiting_network: 'Sem internet — aguardando. A verificação continua sozinha.',
  home_round: 'Rodada {0}/{1} — {2} links ainda sem passar',
  home_next_round: 'A rodada {0} começa em {1}',
  home_recent_label: 'Recentes',
  home_start: 'Iniciar verificação',
  home_run_again: 'Verificar de novo',
  home_stop: 'Parar verificação',
  home_view_results: 'Ver resultados',
  stat_alive: 'Ativos',
  stat_dead: 'Fora do ar',
  stat_response: 'Resposta',

  err_generic_title: 'Algo deu errado',
  err_try_again_later: 'Tente novamente mais tarde.',
  err_key_invalid: 'Chave do dispositivo inválida ou revogada.',
  err_key_required: 'Informe a chave do dispositivo.',
  err_server_unreachable: 'Não foi possível conectar ao servidor. Verifique a rede.',

  a11y_change_language: 'Alterar idioma',
  a11y_copy: 'Copiar',

  settings_language: 'Idioma',
  settings_section_language: 'Idioma',
  settings_measurement: 'Medição',
  settings_measurement_value: '{0} rodadas, {1}s de intervalo — definido pelo servidor',
  settings_measurement_unknown: 'Ainda não recebido do servidor',
  settings_section_device: 'Aparelho',
  device_model: 'Modelo',
  device_app_version: 'Versão do app',
  device_network: 'Rede atual',
  device_carrier: 'Operadora',
  device_region: 'Região',
  device_ip: 'Endereço IP',
  device_summary: '{0} · v{1}',

  filter_all: 'Todos {0}',
  filter_alive: 'Ativos {0}',
  filter_dead: 'Fora {0}',
  result_empty: 'Nenhum link',
  result_copied: 'Copiado',

  keyentry_subtitle: 'Informe a chave do dispositivo para começar',
  keyentry_field_label: 'Chave do dispositivo (dvk_…)',
  keyentry_submit: 'Verificar e salvar',

  settings_section_device_key: 'Chave do aparelho',
  device_key_remove: 'Remover a chave deste aparelho',
  device_key_remove_title: 'Remover a chave deste aparelho?',
  device_key_remove_body:
    'A chave guardada neste celular será apagada. As verificações agendadas param e a execução automática é desligada. Você precisará da chave novamente para configurar o aparelho.',
  device_key_remove_action: 'Remover chave',
  device_key_removing: 'Removendo a chave…',
  device_key_removing_body: 'Parando as verificações ainda em andamento.',

  // --- WebClip-only, translated here (no Android source to copy from) ---------

  ios_net_online: 'Conectado',

  ios_last_check_label: 'Última verificação',
  ios_last_check_never: 'Nada enviado ainda',
  ios_last_check_ago: 'Enviado {0}',
  ios_last_check_at: 'às {0}',
  ios_last_check_stale:
    'Este aparelho está em silêncio há mais de {0} minutos. Verifique: a tela está apagada, o app foi fechado com um deslize, o celular acabou de reiniciar?',
  ios_next_run: 'Próxima execução em {0}',
  ios_next_run_none: 'Nenhuma execução agendada',
  ios_running_now: 'Uma execução está em andamento…',

  ios_ago_just_now: 'agora mesmo',
  ios_ago_seconds: 'há {0}s',
  ios_ago_minutes: 'há {0} min',
  ios_ago_hours: 'há {0} h {1} min',
  ios_ago_days: 'há {0} d {1} h',

  ios_batch_saved: 'Último lote: {0} linhas salvas',
  ios_batch_saved_skipped: 'Último lote: {0} linhas salvas, {1} ignoradas',
  ios_batch_dropped: '{0} links não completaram todas as rodadas — não enviados de propósito',

  ios_phase_fetching: 'Obtendo a configuração e a lista de links…',
  ios_phase_submitting: 'Enviando resultados…',

  // No singular: aqui a palavra qualifica um link, não a contagem da célula de
  // estatística (`stat_alive`, no plural). A interrogação faz parte da palavra
  // traduzida — veja a nota em en.ts (SPEC §13). Mesmo termo explicado em
  // `ios_caveat`.
  ios_status_alive_maybe: 'Ativo?',

  ios_status_unmeasurable: 'não mensurável',
  ios_error_mixed_content:
    'domínio http:// simples — o Safari bloqueia conteúdo misto. Não é um veredito sobre o link.',

  ios_key_revoked_title: 'Chave do dispositivo recusada',
  ios_key_revoked_body:
    'O servidor retornou 401 — esta chave expirou ou o aparelho foi bloqueado. O app parou de vez e não vai tentar de novo.',
  ios_key_revoked_hint:
    'A chave fica na URL do ícone, então não dá para trocá-la de dentro do app. Peça um novo link de instalação a um administrador, apague o ícone antigo e use “Adicionar à Tela de Início” de novo.',

  ios_settings_section_operation: 'Operação',
  ios_settings_schedule: 'Agendamento',
  ios_settings_schedule_value: 'A cada {0} min — definido pelo servidor',
  ios_settings_schedule_default:
    'A cada {0} min — padrão, não foi possível ler a configuração do servidor',
  ios_settings_autorun_off:
    'O servidor está com as verificações automáticas desligadas. O aparelho continua acordando e perguntando, para que possam ser religadas remotamente.',
  ios_settings_foreground_only:
    'Este app só roda com a tela ligada e o WebClip em primeiro plano. Ele não reabre depois de reiniciar. É um limite da plataforma, não um defeito.',

  ios_settings_section_device_setup: 'Configuração obrigatória do aparelho',
  ios_setup_autolock: 'Ajustes → Tela e Brilho → Bloqueio automático: Nunca',
  ios_setup_lowpower: 'Desative o Modo de Baixo Consumo',
  ios_setup_charger: 'Mantenha o aparelho no carregador',
  ios_setup_guided_access: 'Ative o Acesso Guiado, travado neste WebClip',
  ios_setup_note:
    'Nada disso pode ser verificado ou alterado de dentro do app — o Safari não tem acesso.',

  ios_device_platform: 'Plataforma',
  ios_device_user_agent: 'User agent',

  ios_caveat:
    '“Ativo” aqui significa “provavelmente ativo”, não “comprovadamente ativo”. Este app só consegue medir se algo responde; ele não lê código de status e não distingue o site real da página de bloqueio da operadora. Não junte estes números com os do app Android no mesmo nível de confiança.',

  ios_copy_failed: 'Não foi possível copiar',
} satisfies Record<keyof typeof en, string>;
